import { Injectable } from '@angular/core';
import { fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ISessionSettings } from '../../../shared/models/session-settings';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { IdleWarningModalComponent } from '../components/idle-warning-modal/idle-warning-modal.component';
import { AuthService } from '../../../shared/services/auth.service';
import { ConfigurationService } from '../../../shared/services/configuration.service';


@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {

  // Default session settings before retrieving from server (in case of http failure)
  sessionSettings: ISessionSettings = {
    idleTimeInSecond: 300, // Idle time before showing warning
    timeoutInSecond: 60 // Duration before session expires after warning
  };

  subscriptions: Subscription = new Subscription();
  idleTimeoutTimer: Subscription = new Subscription();
  sessionTimeoutTimer: Subscription = new Subscription();

  mouseMove$ : Observable<Event> = fromEvent(document, 'mousemove');
  mouseClick$ : Observable<Event> = fromEvent(document, 'click');
  keyPress$ : Observable<Event> = fromEvent(document, 'keydown');
  interactionEvents$: Observable<Event> = merge(
    this.mouseMove$,
    this.mouseClick$,
    this.keyPress$
  );

  modalDialogClosed$: Subject<void> = new Subject<void>();

  constructor(
    private _configurationService: ConfigurationService,
    private _authService: AuthService,
    private _modalService: NgbModal
  ) {

      this.setupInteractionEventsSubscription();
  }

  setupInteractionEventsSubscription(): void {
    // Load session settings from configuration service
    this.subscriptions.add(this._configurationService.getSessionSettings().pipe(
      switchMap((response) => {
        if (response.success) {
          this.sessionSettings = response.content;
        } else {
          // Fallback to default settings
          console.error('Failed to load session settings:', response.message);
        }

        return this.interactionEvents$;
      })
    )
    .subscribe({
      next: (event) => {
        // Reset idle warning timer on any user interaction
        this.startIdleWarningTimer();
      },
      error: (error) => {
        console.error('Error setting up idle warning subscription:', error);
      }
    }));
  }

  startIdleWarningTimer(): void {
    this.idleTimeoutTimer.unsubscribe(); // Clear any existing timer

    const idleTimeoutMs: number = this.sessionSettings.idleTimeInSecond * 1000;

    this.idleTimeoutTimer = timer(idleTimeoutMs).pipe(
      takeUntil(this.interactionEvents$)
    )
    .subscribe(() => {
      // Show idle warning modal after idle timeout
      this.startSessionTimeoutTimer();
    });

    this.subscriptions.add(this.idleTimeoutTimer);
  }

  startSessionTimeoutTimer(): void {

    // After showing the warning, start the timeout timer
    const modalRef = this.openIdleWarningModal();

    this.sessionTimeoutTimer.unsubscribe(); // Clear any existing timer

    const sessionTimeoutMs: number = this.sessionSettings.timeoutInSecond * 1000;

    this.sessionTimeoutTimer = timer(sessionTimeoutMs).pipe(
      takeUntil(this.modalDialogClosed$)
    )
    .subscribe(() => {
      // If the user does not interact with the modal, log them out after timeout
      this.closeIdleWarningModal(modalRef);
      this.logout();
    });

    this.subscriptions.add(this.sessionTimeoutTimer);
  }

  openIdleWarningModal(): NgbModalRef {
    this._modalService.dismissAll(); // Close any existing modal dialogs
    const modalRef = this._modalService.open(IdleWarningModalComponent, { centered: true });

    modalRef.componentInstance.onCountdownChanged(this.sessionSettings.timeoutInSecond);

    const logoutClickedSubscription = modalRef.componentInstance.logoutClicked.subscribe({
      next: () => {
        this.closeIdleWarningModal(modalRef);
        this.logout();
      }
    });

    const continueWorkingClickedSubscription = modalRef.componentInstance.continueWorkingClicked.subscribe({
      next: () => {
        this.closeIdleWarningModal(modalRef);
      }
    });

    modalRef.result.finally(() => {
      logoutClickedSubscription.unsubscribe();
      continueWorkingClickedSubscription.unsubscribe();

      this.modalDialogClosed$.next(); // Notify that the modal dialog is closed
    })

    return modalRef;
  }

  closeIdleWarningModal(modalRef: NgbModalRef): void {
    modalRef.close();
    //this.modalDialogClosed$.next(); // Notify that the modal dialog is closed
  }

  logout(): void {
    this._authService.logout();
  }
}
